import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { AlertCircleOutlineIcon } from '@components/icons/AlertCircleOutlineIcon';
import { AccordionChevron, AccordionContent, AccordionItem } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useRetryAsyncTask } from '../../hooks/useRetryAsyncTask';
import { useTranslation } from '../../hooks/useTranslation';
import { JobCardItem } from './JobCardItem';
import { JobStatusIcon } from './JobStatusIcon';
import { ScrollbarHairlines } from './ScrollbarHairlines';
import { CUSTOM_SCROLLBAR_CLASSES } from './constants';
import { formatJobTimestamp } from './formatJobTimestamp';
import { useIsScrollable } from './useIsScrollable';
import type { ApiAsyncTask, ApiAsyncTaskItem, AsyncTaskState } from '@api/asyncTasks';

// Caps the expanded list at 8 visible rows per Figma:
//   8 × size-16 (48px) rows + 7 × gap-4 (12px) + 2 × p-6 (18px) = 504px
const EXPANDED_LIST_MAX_HEIGHT = 'max-h-[504px]';

interface JobCardProps {
  task: ApiAsyncTask;
}

export function JobCard({ task }: JobCardProps) {
  const { t } = useTranslation();
  const [setListRef, isListScrollable] = useIsScrollable<HTMLUListElement>();
  const timestamp = task.completed_at
    ? t('job_card.completed_on', { date: formatJobTimestamp(task.completed_at) })
    : t('job_card.queued_on', { date: formatJobTimestamp(task.created_at) });
  const items = sortItems(task.items);
  const expandable = items.length > 0;
  const hasFailure =
    task.state === 'failed' ||
    (task.state === 'partial_success' && task.failed_count > 0);
  // Retry is only for the queue-level failure case — the task errored before
  // any item ran (e.g. validation rejected the whole batch). When individual
  // items failed during processing there's nothing to retry at the task level;
  // the user needs to fix each item.
  const canRetry =
    task.state === 'failed' && task.failed_count === 0 && task.retryable;

  return (
    <AccordionItem
      value={`job-${task.id}`}
      disabled={!expandable}
      className="rounded-input border border-dinnerplate-white shadow-soft overflow-hidden bg-off-white isolate"
    >
      <AccordionPrimitive.Header className="flex">
        <AccordionPrimitive.Trigger className="group/accordion flex flex-1 p-6 font-inter text-off-black text-left data-[disabled]:cursor-default">
          <div className="flex flex-col items-start gap-1 min-w-0 flex-1 w-full">
            <div className="flex items-center justify-between gap-2 w-full min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-lg font-semibold leading-normal truncate">
                  {t('job_card.title_with_counts', {
                    title: task.title,
                    completed: String(task.success_count),
                    total: String(task.item_count),
                  })}
                </span>
                <JobStatusIcon state={task.state} className="shrink-0" />
                <span className="sr-only">{t(`job_card.state.${task.state}`)}</span>
              </div>
              {expandable && (
                <AccordionChevron
                  data-testid="job-card-chevron"
                  className="h-[10px] w-[18px]"
                />
              )}
            </div>
            <span className="text-xs font-normal leading-tight-sm text-grey truncate w-full">
              {timestamp}
            </span>
          </div>
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
      {hasFailure && (
        <div className="flex items-center justify-between gap-3 px-6 pb-6 -mt-3">
          <HeaderFailureChip task={task} />
          {canRetry && <RetryButton task={task} />}
        </div>
      )}
      <AccordionContent>
        {items.length > 0 && (
          <>
            <div aria-hidden="true" className="h-px bg-dinnerplate-white" />
            <div className="relative">
              {isListScrollable && <ScrollbarHairlines />}
              <ul
                ref={setListRef}
                className={cn(
                  'flex flex-col gap-4 p-6 bg-off-white overflow-y-auto overflow-x-hidden [scrollbar-gutter:stable]',
                  EXPANDED_LIST_MAX_HEIGHT,
                  CUSTOM_SCROLLBAR_CLASSES,
                )}
              >
                {items.map((item) => (
                  <JobCardItem key={item.id} item={item} />
                ))}
              </ul>
            </div>
          </>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

function RetryButton({ task }: { task: ApiAsyncTask }) {
  const { t } = useTranslation();
  const { mutate, isPending } = useRetryAsyncTask();

  return (
    <Button
      variant="outline"
      size="sm"
      disabled={isPending}
      onClick={() => mutate(task.id)}
      aria-label={t('job_card.retry_aria', { title: task.title })}
      className="h-auto rounded-xs border-dinnerplate-white bg-off-white px-3 py-1.5 text-sm font-inter font-semibold text-off-black hover:bg-dinnerplate-white-light"
    >
      {t('job_card.retry')}
    </Button>
  );
}

function HeaderFailureChip({ task }: { task: ApiAsyncTask }) {
  const { t } = useTranslation();

  const isFullFailure = task.state === 'failed';
  const isPartialFailure = task.state === 'partial_success' && task.failed_count > 0;
  if (!isFullFailure && !isPartialFailure) return null;

  // Three cases:
  //   1. failed + failed_count === 0 — job errored before any items ran
  //      (e.g. validation rejected the whole batch). Count-based copy would
  //      render "0 items", so use a distinct message.
  //   2. failed_count === 1 — avoid the "1 items" grammar bug.
  //   3. otherwise — standard pluralized summary.
  const message =
    isFullFailure && task.failed_count === 0
      ? t('job_card.job_errored')
      : task.failed_count === 1
        ? t('job_card.failure_summary_one')
        : t('job_card.failure_summary', { count: task.failed_count });

  return (
    <div className="flex items-center gap-2 bg-chilli-blush rounded-xs px-4 py-2">
      <AlertCircleOutlineIcon aria-hidden="true" className="size-[14px] text-chilli-red shrink-0" />
      <span className="text-sm font-semibold leading-normal text-chilli-red">
        {message}
      </span>
    </div>
  );
}

const STATE_RANK: Record<AsyncTaskState, number> = {
  failed: 0,
  in_progress: 1,
  pending: 2,
  succeeded: 3,
  partial_success: 4,
};

function sortItems(items: ApiAsyncTaskItem[] | undefined): ApiAsyncTaskItem[] {
  if (!items || items.length === 0) return [];
  return [...items].sort((a, b) => {
    const byState = STATE_RANK[a.state] - STATE_RANK[b.state];
    if (byState !== 0) return byState;
    return a.position - b.position;
  });
}
